import { VipTiers } from '@maxduel/constants';
import getIdToken from '@/helpers/firebase-token';
import axios from '@/helpers/axios-config';
import { formatBalance } from '@/helpers/utils';

const API_URL = process.env.VUE_APP_API_URL;
const SOCKET_MESSAGE_LEVEL_UP = 'leveled_up';
const SOCKET_MESSAGE_UPDATED = 'updated';

const isGloballyEnabled = process.env.VUE_APP_VIP_CLUB_ENABLED === 'true';

const state = {
  isEnabled: isGloballyEnabled,
  config: [
    {
      id: VipTiers.Iron,
      pointsMin: 0,
      pointsMax: 99,
      rakeback: 0,
      cap: 0,
    },
    {
      id: VipTiers.Steel,
      pointsMin: 100,
      pointsMax: 199,
      rakeback: 2.5,
      cap: 100,
    },
    {
      id: VipTiers.Bronze,
      pointsMin: 200,
      pointsMax: 499,
      rakeback: 5,
      cap: 500,
    },
    {
      id: VipTiers.Silver,
      pointsMin: 500,
      pointsMax: 1499,
      rakeback: 10,
      cap: 2000,
    },
    {
      id: VipTiers.Gold,
      pointsMin: 1500,
      pointsMax: 3999,
      rakeback: 16,
      cap: 3000,
    },
    {
      id: VipTiers.Platinum,
      pointsMin: 4000,
      pointsMax: 9999,
      rakeback: 22,
      cap: 4000,
    },
    {
      id: VipTiers.Diamond,
      pointsMin: 10000,
      pointsMax: Infinity,
      rakeback: 30,
      cap: 5000,
    },
  ],
  currentPoints: 0,
  currentTier: VipTiers.Iron,
  rewardList: [],
  nextMonthTimestamp: 0,
  showLevelUpPopup: false,
};

const getters = {
  getTierConfig: (state) => (id) => state.config.find((tier) => tier.id === id),

  getNextTier(state, getters) {
    return getters.getNextTierConfig(state.currentTier)?.id;
  },

  getNextTierConfig: (state) => (id) => {
    const tierIdx = state.config.findIndex((tier) => tier.id === id);
    if (tierIdx === -1 || tierIdx + 1 >= state.config.length) return null;
    return state.config[tierIdx + 1];
  },

  getPointsToNextTier(state, getters) {
    const tierConfig = getters.getTierConfig(state.currentTier);
    if (!tierConfig || tierConfig.pointsMax === Infinity) return 0;
    return tierConfig.pointsMax - state.currentPoints + 1;
  },

  getMsToNextMonth: (state) => state.nextMonthTimestamp - Date.now(),

  getFormattedAvailableRewardList(state, getters) {
    return state.rewardList
      .filter((reward) => reward.available)
      .map((reward) => ({
        ...reward,
        amount: formatBalance(reward.amount),
        rakeback: getters.getTierConfig(reward.level)?.rakeback || 0,
      }));
  },

  getLatestUnclaimedReward: (state) => state.rewardList.find((reward) => !reward.available),

  getProgressWidth(state, getters) {
    const tierConfig = getters.getTierConfig(state.currentTier);
    if (!tierConfig || tierConfig.pointsMax === Infinity) return '100%';

    const fullRange = tierConfig.pointsMax - tierConfig.pointsMin;
    const currentProgress = state.currentPoints - tierConfig.pointsMin;
    return `${(currentProgress / fullRange) * 100}%`;
  },
};

const actions = {
  async getVipStatus({ commit }) {
    const sessionKey = await getIdToken();
    const config = {
      method: 'post',
      url: `${API_URL}/vip/getStatus`,
      data: { sessionKey },
    };

    try {
      const { data } = await axios.request(config);

      if (data.hasError || !data.data) {
        throw new Error('Failed to get vipStatus data');
      }

      commit('SET_CURRENT_POINTS', data.data.points);
      commit('SET_CURRENT_TIER', data.data.level);
      commit('SET_REWARD_LIST', data.data.bonuses);
      commit('SET_NEXT_MONTH_TIMESTAMP', Date.now() + Math.round(data.data.tillNextMonth));
    } catch (error) {
      console.error(error?.response?.data || error.message);
    }
  },

  async processVipSocketEvent({ dispatch }, message) {
    if (message === SOCKET_MESSAGE_UPDATED) dispatch('getVipStatus');
    else if (message === SOCKET_MESSAGE_LEVEL_UP) dispatch('setShowLevelUpPopup', true);
  },

  setShowLevelUpPopup({ commit }, value) {
    commit('SET_SHOW_LEVEL_UP_POPUP', value);
  },
};

const mutations = {
  SET_CURRENT_POINTS(state, points) {
    state.currentPoints = points;
  },

  SET_CURRENT_TIER(state, tier) {
    state.currentTier = tier;
  },

  SET_REWARD_LIST(state, list) {
    state.rewardList = list;
  },

  SET_NEXT_MONTH_TIMESTAMP(state, timestamp) {
    state.nextMonthTimestamp = timestamp;
  },

  SET_SHOW_LEVEL_UP_POPUP(state, value) {
    state.showLevelUpPopup = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
